import React, { useState } from "react";
import {
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { IContractParty } from "dto/Contracts/IContract";
import { axiosInstance } from "api/axios";
import { NIPMaskedInput } from "./MaskedInputs";
import styles from "./styles.module.scss";

const NIP_REGEX = /^\d{10}$/;

const PODMIOT_OPTIONS = ["Przedsiębiorca", "Osoba fizyczna", "JSFP"];

const CONFIDENTIALITY_BASES = [
  { value: "DIP_art5_ust1",  label: "Art. 5 ust. 1"  },
  { value: "DIP_art5_ust2",  label: "Art. 5 ust. 2"  },
  { value: "DIP_art5_ust2a", label: "Art. 5 ust. 2a" },
  { value: "DIP_art5_ust2b", label: "Art. 5 ust. 2b" },
  { value: "Other",          label: "Inna podstawa wyłączenia jawności" },
];

const emptyParty = (): IContractParty => ({
  Name: "",
  IdentifierType: "NIP",
  IdentifierValue: "",
  Role: "",
});

// Symulacja danych bieżącego użytkownika aplikacji
const CURRENT_USER_PARTY: IContractParty = {
  Name: "Urząd Gminy Przykładowo",
  IdentifierType: "NIP",
  IdentifierValue: "5260250274",
  Role: "JSFP",
};

const genId = () => `p-${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface PartyTab {
  id: string;
  data: IContractParty;
  saved: boolean;
  hidden: boolean;
  hiddenBasis: string;
}

interface Props {
  onDataChange: (parties: IContractParty[]) => void;
}

const PartiesCard: React.FC<Props> = ({ onDataChange }) => {
  const firstId = useState(() => genId())[0];
  const secondId = useState(() => genId())[0];

  const [tabs, setTabs] = useState<PartyTab[]>([
    { id: firstId, data: CURRENT_USER_PARTY, saved: true,  hidden: false, hiddenBasis: "" },
    { id: secondId, data: emptyParty(),       saved: false, hidden: false, hiddenBasis: "" },
  ]);
  const [savedOrder, setSavedOrder] = useState<string[]>([firstId]);
  const [activeTabIdx, setActiveTabIdx] = useState(2);
  const [nipLoadingTabsIdx, setNipLoadingTabsIdx] = useState<number | null>(null);

  const report = (currentTabs: PartyTab[], currentSavedOrder: string[]) => {
    const parties = currentSavedOrder
      .map((id) => currentTabs.find((t) => t.id === id))
      .filter((t): t is PartyTab => !!t)
      .map((t) => ({
        ...t.data,
        ...(t.hidden && t.hiddenBasis ? { ConfidentialityBasis: t.hiddenBasis } : {}),
      }));
    onDataChange(parties);
  };

  const handleFieldChange = (tabsIdx: number, field: keyof IContractParty, value: string) => {
    setTabs((prev) => prev.map((t, i) => (i === tabsIdx ? { ...t, data: { ...t.data, [field]: value } } : t)));
  };

  const handleNipBlur = async (tabsIdx: number, value: string) => {
    const tab = tabs[tabsIdx];
    if (tab.data.IdentifierType !== "NIP") return;
    const clean = value.replace(/[-\s]/g, "");
    if (!NIP_REGEX.test(clean) || tab.data.Name.trim()) return;

    setNipLoadingTabsIdx(tabsIdx);
    try {
      const res = await axiosInstance.get(`/bir/search?nip=${clean}`);
      const name: string = res?.data?.Name ?? "";
      if (name) handleFieldChange(tabsIdx, "Name", name);
    } catch {
      // silent
    } finally {
      setNipLoadingTabsIdx(null);
    }
  };

  const handleSave = (tabsIdx: number) => {
    const tab = tabs[tabsIdx];
    const isFirst = !tab.saved;

    const newTabs = tabs.map((t, i) => (i === tabsIdx ? { ...t, saved: true } : t));
    const newSavedOrder = isFirst ? [...savedOrder, tab.id] : savedOrder;

    let finalTabs = newTabs;
    let nextActive = activeTabIdx;

    if (isFirst && tabsIdx === tabs.length - 1) {
      finalTabs = [...newTabs, { id: genId(), data: emptyParty(), saved: false, hidden: false, hiddenBasis: "" }];
      nextActive = activeTabIdx + 1;
    }

    setTabs(finalTabs);
    setSavedOrder(newSavedOrder);
    setActiveTabIdx(nextActive);
    report(finalTabs, newSavedOrder);
  };

  const handleToggleHidden = (id: string) => {
    const newTabs = tabs.map((t) =>
      t.id === id ? { ...t, hidden: !t.hidden, hiddenBasis: t.hidden ? "" : t.hiddenBasis } : t
    );
    setTabs(newTabs);
    report(newTabs, savedOrder);
  };

  const handleSetHiddenBasis = (id: string, basis: string) => {
    const newTabs = tabs.map((t) => (t.id === id ? { ...t, hiddenBasis: basis } : t));
    setTabs(newTabs);
    report(newTabs, savedOrder);
  };

  const handleMoveUp = (savedIdx: number) => {
    if (savedIdx === 0) return;
    const newOrder = [...savedOrder];
    [newOrder[savedIdx - 1], newOrder[savedIdx]] = [newOrder[savedIdx], newOrder[savedIdx - 1]];
    setSavedOrder(newOrder);
    report(tabs, newOrder);
  };

  const handleMoveDown = (savedIdx: number) => {
    if (savedIdx >= savedOrder.length - 1) return;
    const newOrder = [...savedOrder];
    [newOrder[savedIdx], newOrder[savedIdx + 1]] = [newOrder[savedIdx + 1], newOrder[savedIdx]];
    setSavedOrder(newOrder);
    report(tabs, newOrder);
  };

  const handleRemove = (tabsIdx: number) => {
    if (tabsIdx === 0) return;
    if (tabs.length <= 1) return;
    const tab = tabs[tabsIdx];
    const newTabs = tabs.filter((_, i) => i !== tabsIdx);
    const newSavedOrder = savedOrder.filter((id) => id !== tab.id);
    const muiIdx = tabsIdx + 1;
    const nextActive = Math.min(Math.max(1, muiIdx <= activeTabIdx ? activeTabIdx - 1 : activeTabIdx), newTabs.length);
    setTabs(newTabs);
    setSavedOrder(newSavedOrder);
    setActiveTabIdx(nextActive);
    report(newTabs, newSavedOrder);
  };

  const savedTabs = savedOrder.map((id) => tabs.find((t) => t.id === id)).filter((t): t is PartyTab => !!t);
  const currentTabsIdx = activeTabIdx - 1;
  const currentTab = activeTabIdx > 0 ? tabs[currentTabsIdx] : null;
  const isUserTab = activeTabIdx === 1;

  return (
    <div className={styles.consortiumCard}>
      {/* Nagłówek */}
      <div className={`${styles.consortiumHeader} ${styles.partiesHeader}`}>
        <span className={styles.consortiumHeaderLabel}>Strony umowy</span>
      </div>

      {/* Pasek zakładek */}
      <div className={styles.consortiumTabsBar}>
        <Tabs
          value={activeTabIdx}
          onChange={(_, v) => setActiveTabIdx(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 30,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { gap: "3px", padding: "5px 10px 0" },
            "& .MuiTab-root": {
              minHeight: 28,
              fontSize: 11,
              padding: "3px 12px",
              textTransform: "none",
              border: "1px solid #c5d5f5",
              borderRadius: "4px 4px 0 0",
              color: "#888",
            },
            "& .MuiTab-root.Mui-selected": {
              background: "#e8f0fe",
              color: "#1a56c4",
              borderColor: "#1a56c4",
              fontWeight: 600,
            },
          }}
        >
          <Tab label="Lista" />
          {tabs.map((_, idx) => {
            const isLast = idx === tabs.length - 1;
            return (
              <Tab
                key={idx}
                label={isLast ? "+" : String(idx + 1)}
                className={isLast ? styles.plusTabBtn : undefined}
              />
            );
          })}
        </Tabs>
      </div>

      {/* Zawartość zakładki */}
      <div className={styles.consortiumTabContent}>
        {activeTabIdx === 0 ? (
          savedTabs.length === 0 ? (
            <span className={styles.consortiumEmptyList}>Brak zapisanych stron</span>
          ) : (
            <div className={styles.consortiumMemberList}>
              {savedTabs.map((t, savedIdx) => (
                <div
                  key={t.id}
                  className={`${styles.consortiumMemberItem} ${t.hidden ? styles.consortiumMemberItemHidden : ""}`}
                  onClick={() => {
                    const ti = tabs.findIndex((tab) => tab.id === t.id);
                    if (ti >= 0) setActiveTabIdx(ti + 1);
                  }}
                >
                  <div className={styles.consortiumMemberReorder}>
                    <button type="button" className={styles.reorderBtn} onClick={(e) => { e.stopPropagation(); handleMoveUp(savedIdx); }} disabled={savedIdx === 0}>▲</button>
                    <button type="button" className={styles.reorderBtn} onClick={(e) => { e.stopPropagation(); handleMoveDown(savedIdx); }} disabled={savedIdx === savedTabs.length - 1}>▼</button>
                  </div>
                  <span className={styles.consortiumMemberName}>{t.data.Name.trim() || `Strona ${savedIdx + 1}`}</span>
                  {t.id === tabs[0]?.id && <span className={styles.userBadge}>użytkownik</span>}
                  <button
                    type="button"
                    className={`${styles.hideToggleBtn} ${t.hidden ? styles.hideToggleBtnActive : ""}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleHidden(t.id); }}
                  >
                    {t.hidden ? "Cofnij" : "Ukryj"}
                  </button>
                  {t.hidden && (
                    <FormControl
                      size="small"
                      className={styles.subjectBasisSelect}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <InputLabel>Podstawa wyłączenia jawności</InputLabel>
                      <Select
                        value={t.hiddenBasis}
                        label="Podstawa wyłączenia jawności"
                        onChange={(e) => handleSetHiddenBasis(t.id, e.target.value)}
                      >
                        {CONFIDENTIALITY_BASES.map((b) => (
                          <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </div>
              ))}
            </div>
          )
        ) : currentTab ? (
          <div className={styles.consortiumMemberForm}>
            {isUserTab && (
              <div className={styles.userTabInfo}>Dane bieżącego użytkownika aplikacji</div>
            )}
            <div className={styles.partyRow1}>
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <Select
                  value={currentTab.data.IdentifierType}
                  onChange={(e) => handleFieldChange(currentTabsIdx, "IdentifierType", e.target.value as "NIP" | "PESEL")}
                >
                  <MenuItem value="NIP">NIP</MenuItem>
                  <MenuItem value="PESEL">PESEL</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Numer identyfikatora"
                size="small"
                sx={{ flex: 1 }}
                value={currentTab.data.IdentifierValue}
                onChange={(e) => handleFieldChange(currentTabsIdx, "IdentifierValue", e.target.value)}
                onBlur={(e) => handleNipBlur(currentTabsIdx, e.target.value)}
                InputProps={{
                  ...(currentTab.data.IdentifierType === "NIP" && {
                    inputComponent: NIPMaskedInput as React.ElementType,
                  }),
                  ...(nipLoadingTabsIdx === currentTabsIdx && {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ),
                  }),
                }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Typ podmiotu</InputLabel>
                <Select
                  value={currentTab.data.Role}
                  label="Typ podmiotu"
                  onChange={(e) => handleFieldChange(currentTabsIdx, "Role", e.target.value)}
                >
                  {PODMIOT_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <TextField
              label="Nazwa"
              size="small"
              fullWidth
              value={currentTab.data.Name}
              onChange={(e) => handleFieldChange(currentTabsIdx, "Name", e.target.value)}
              placeholder={currentTab.data.IdentifierType === "NIP" ? "Uzupełniane automatycznie z NIP" : ""}
            />

            <div className={styles.consortiumMemberActions}>
              <div />
              <div className={styles.consortiumMemberActionsRight}>
                {!isUserTab && tabs.length > 2 && (
                  <button type="button" className={styles.removeBtn} onClick={() => handleRemove(currentTabsIdx)}>
                    Usuń
                  </button>
                )}
                <button type="button" className={styles.saveMemberBtn} onClick={() => handleSave(currentTabsIdx)}>
                  {currentTab.saved ? "Zaktualizuj" : "Zapisz"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PartiesCard;
